import React, { useState, useEffect, useCallback } from "react"
import { Plus, Pencil, Power, Search, Bell, X, Check } from "lucide-react"
import "../styles/leaveTypes.css";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api/leave-types`;

const EMPTY_FORM = {
  name: "",
  code: "",
  unit_type: "DAY",
  accrual_type: "YEARLY",
  max_consecutive_days: "",
  gender_restriction: "All",
  carry_forward_allowed: false,
  max_carry_forward: 0,
  requires_document: false,
  reporting_head_override: false,
  is_active: true,
}

// Map DB row → UI-friendly shape
function dbToUI(lt) {
  return {
    id: lt.id,
    name: lt.name,
    code: lt.code,
    unit_type: lt.unit_type,
    accrual_type: lt.accrual_type,
    max_consecutive_days: lt.max_consecutive_days ?? "",
    gender_restriction: lt.gender_restriction ?? "All",
    carry_forward_allowed: lt.carry_forward_allowed,
    max_carry_forward: lt.max_carry_forward,
    requires_document: lt.requires_document,
    reporting_head_override: lt.reporting_head_override ?? lt.reportingHeadOverride ?? false,
    is_active: lt.is_active,
  }
}

// Map UI form → API payload
function uiToDB(form) {
  return {
    name: form.name.trim(),
    code: form.code.trim().toUpperCase(),
    unit_type: form.unit_type,
    accrual_type: form.accrual_type,
    max_consecutive_days: form.max_consecutive_days === "" ? null : Number(form.max_consecutive_days),
    gender_restriction: form.gender_restriction === "All" ? null : form.gender_restriction,
    carry_forward_allowed: form.carry_forward_allowed,
    max_carry_forward: Number(form.max_carry_forward),
    requires_document: form.requires_document,
    reporting_head_override: form.reporting_head_override,
    is_active: form.is_active,
  }
}

function Toggle({ value, onChange }) {
  return (
    <button
      className={`lt-toggle-track ${value ? "on" : "off"}`}
      onClick={() => onChange(!value)}
      type="button"
    >
      <div className={`lt-toggle-thumb ${value ? "on" : "off"}`} />
    </button>
  )
}

export default function LeaveTypesPage({ isTab = false }) {
  // Existing states
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [leaveTypes, setLeaveTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})
  const [search, setSearch] = useState("")
  const [toggleId, setToggleId] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  // NEW: Dynamic Dropdown States
  const [dropdowns, setDropdowns] = useState({
    UintType: [],
    Accrualtype: [],
    GenderRestriction: []
  })
  const [dropdownsLoading, setDropdownsLoading] = useState({
    UintType: false,
    Accrualtype: false,
    GenderRestriction: false
  })

  const { token } = useAuth()

  // API Endpoints
  const DROPDOWN_APIS = {
    UintType: `${process.env.REACT_APP_API_BASE_URL}/api/dropdowns/values/UintType`,
    Accrualtype: `${process.env.REACT_APP_API_BASE_URL}/api/dropdowns/values/Accrualtype`,
    GenderRestriction: `${process.env.REACT_APP_API_BASE_URL}/api/dropdowns/values/GenderRestriction`
  }

  const toggleSidebar = () => setSidebarOpen(prev => !prev)

  // ── Toast (Now using react-hot-toast) ──
  const showToast = (msg, type = "success") => {
    if (type === "success") {
      toast.success(msg);
    } else {
      toast.error(msg);
    }
  }

  // ── Auth headers ──
  const authHeaders = useCallback(() => ({
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token])

  // ── Fetch all leave types ──
  const fetchLeaveTypes = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(API_BASE, { headers: authHeaders() })
      const ct = res.headers.get("content-type") || ""
      if (!ct.includes("application/json")) {
        throw new Error(`Expected JSON but got HTML (status ${res.status}). Check REACT_APP_API_BASE_URL.`)
      }
      if (!res.ok) throw new Error("Failed to fetch")
      const data = await res.json()
      setLeaveTypes(data.map(dbToUI))
    } catch (err) {
      console.error(err)
      showToast(err.message || "Failed to load leave types.", "error")
    } finally {
      setLoading(false)
    }
  }, [authHeaders])

  // ── NEW: Fetch single dropdown ──
  const fetchDropdown = async (dropdownKey, url) => {
    setDropdownsLoading(prev => ({ ...prev, [dropdownKey]: true }))
    
    try {
      const res = await fetch(url, { headers: authHeaders() })
      if (!res.ok) throw new Error(`Failed to fetch ${dropdownKey}`)
      
      const data = await res.json()
      setDropdowns(prev => ({ ...prev, [dropdownKey]: data }))
    } catch (err) {
      console.error(err)
      showToast(`Failed to load ${dropdownKey}`, "error")
      
      // Fallback data
      const fallback = {
        UintType: [
          { dropdown_value_code: "DAY", dropdown_value_name: "Days" }, 
          { dropdown_value_code: "HOUR", dropdown_value_name: "Hours" }
        ],
        Accrualtype: [
          { dropdown_value_code: "YEARLY", dropdown_value_name: "Annual" },
          { dropdown_value_code: "MONTHLY", dropdown_value_name: "Monthly" },
          { dropdown_value_code: "ONE_TIME", dropdown_value_name: "One-Time" },
          { dropdown_value_code: "NONE", dropdown_value_name: "None" }
        ],
        GenderRestriction: [
          { dropdown_value_code: "All", dropdown_value_name: "All" },
          { dropdown_value_code: "Male", dropdown_value_name: "Male" },
          { dropdown_value_code: "Female", dropdown_value_name: "Female" }
        ]
      }
      setDropdowns(prev => ({ ...prev, [dropdownKey]: fallback[dropdownKey] }))
    } finally {
      setDropdownsLoading(prev => ({ ...prev, [dropdownKey]: false }))
    }
  }

  // ── NEW: Fetch all dropdowns ──
  const fetchAllDropdowns = useCallback(async () => {
    const promises = Object.entries(DROPDOWN_APIS).map(([key, url]) => 
      fetchDropdown(key, url)
    )
    await Promise.all(promises)
  }, [authHeaders])

  // ── NEW: Dropdown helpers ──
  const getDropdownOptions = (type) => dropdowns[type] || []
  const getDisplayName = (type, code) => {
    const option = getDropdownOptions(type).find(opt => opt.dropdown_value_code === code)
    return option?.dropdown_value_name || code
  }

  // ── Updated useEffect ──
  useEffect(() => {
    fetchLeaveTypes()
    fetchAllDropdowns()
  }, [fetchLeaveTypes, fetchAllDropdowns])

  // ── Open modals ──
  const openCreate = () => {
    setFormData(EMPTY_FORM)
    setEditItem(null)
    setErrors({})
    setIsOpen(true)
  }

  const openEdit = (item) => {
    setFormData({ ...item })
    setEditItem(item)
    setErrors({})
    setIsOpen(true)
  }

  // ── Validation ──
  const validate = () => {
    const e = {}
    if (!formData.name.trim()) e.name = "Name is required"
    if (!formData.code.trim()) e.code = "Code is required"
    setErrors(e)
    return Object.keys(e).length === 0
  }

  // ── Create / Update ──
  const handleSubmit = async () => {
    if (!validate()) return
    setSubmitting(true)
    const payload = uiToDB(formData)

    try {
      if (editItem) {
        const res = await fetch(`${API_BASE}/${editItem.id}`, {
          method: "PUT",
          headers: authHeaders(),
          body: JSON.stringify(payload),
        })
        if (!res.ok) {
          const err = await res.json()
          throw new Error(err.message || "Update failed")
        }
        const { leaveType } = await res.json()
        setLeaveTypes(prev =>
          prev.map(lt => lt.id === editItem.id ? dbToUI(leaveType) : lt)
        )
        showToast("Leave type updated successfully!")
      } else {
        const res = await fetch(API_BASE, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify(payload),
        })
        if (!res.ok) {
          const err = await res.json()
          throw new Error(err.message || "Create failed")
        }
        const { leaveType } = await res.json()
        setLeaveTypes(prev => [dbToUI(leaveType), ...prev])
        showToast("Leave type created successfully!")
      }
      setIsOpen(false)
    } catch (err) {
      console.error(err)
      showToast(err.message || "Something went wrong.", "error")
    } finally {
      setSubmitting(false)
    }
  }

  // ── Toggle active status ──
  const handleToggle = async (id) => {
    const target = leaveTypes.find(lt => lt.id === id)
    if (!target) return

    const payload = uiToDB({ ...target, is_active: !target.is_active })

    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error("Toggle failed")
      const { leaveType } = await res.json()
      setLeaveTypes(prev =>
        prev.map(lt => lt.id === id ? dbToUI(leaveType) : lt)
      )
      showToast("Status updated!", "info")
    } catch (err) {
      console.error(err)
      showToast("Failed to update status.", "error")
    } finally {
      setToggleId(null)
    }
  }

  const set = (key, val) => setFormData(f => ({ ...f, [key]: val }))

  const filtered = leaveTypes.filter(lt =>
    lt.name.toLowerCase().includes(search.toLowerCase()) ||
    lt.code.toLowerCase().includes(search.toLowerCase())
  )

  // ── UPDATED: Dynamic display helpers ──
  const displayUnitType = (code) => getDisplayName('UintType', code)
  const displayAccrual = (code) => getDisplayName('Accrualtype', code)

  const pageContent = (
    <>
    <div className="lt-wrap" style={isTab ? { padding: 0 } : {}}>
      {!isTab && (
        <header className="lt-topbar">
          <div className="lt-topbar-left">
            <h1>Leave Types</h1>
            <p>Configure leave type definitions</p>
          </div>
          <div className="lt-topbar-right">
            <div className="lt-search-wrap">
              <Search size={14} className="lt-search-icon" />
              <input
                className="lt-search-input"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
              />
            </div>
            <div className="lt-bell">
              <Bell size={20} />
              <span className="lt-bell-badge">3</span>
            </div>
          </div>
        </header>
      )}

      <main className="lt-content" style={isTab ? { padding: 0, marginTop: 0 } : {}}>
              <div className="lt-section-header">
                <div>
                  <h2>All Leave Types</h2>
                  <p>{leaveTypes.length} leave types configured</p>
                </div>
                <button className="lt-add-btn" onClick={openCreate}>
                  <Plus size={15} /> Add Leave Type
                </button>
              </div>

              <div className="lt-table-card">
                {loading ? (
                  <div style={{ padding: "2rem", textAlign: "center", color: "#888" }}>
                    Loading leave types…
                  </div>
                ) : (
                  <table className="lt-table">
                    <thead>
                      <tr>
                        {["Name", "Code", "Unit Type", "Accrual", "Carry Forward", "Direct Head", "Status", "Actions"].map(col => (
                          <th key={col}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr><td colSpan={8} className="lt-empty">No leave types found.</td></tr>
                      ) : (
                        filtered.map(lt => (
                          <tr key={lt.id}>
                            <td className="col-name">{lt.name}</td>
                            <td><span className="lt-code-badge">{lt.code}</span></td>
                            <td className="col-muted">{displayUnitType(lt.unit_type)}</td>
                            <td className="col-muted">{displayAccrual(lt.accrual_type)}</td>
                            <td>
                              {lt.carry_forward_allowed
                                ? <span className="lt-badge badge-carry">Yes (max {lt.max_carry_forward})</span>
                                : <span className="col-muted">No</span>
                              }
                            </td>
                            <td>
                              {lt.reporting_head_override
                                ? <span className="lt-badge badge-info">Yes</span>
                                : <span className="col-muted">No</span>
                              }
                            </td>
                            <td>
                              {lt.is_active
                                ? <span className="lt-badge badge-active">Active</span>
                                : <span className="lt-badge badge-inactive">Inactive</span>
                              }
                            </td>
                            <td>
                              <div className="lt-actions">
                                <button className="lt-icon-btn edit" onClick={() => openEdit(lt)} title="Edit">
                                  <Pencil size={14} />
                                </button>
                                <button
                                  className={`lt-icon-btn power ${lt.is_active ? "active" : "inactive"}`}
                                  onClick={() => setToggleId(lt.id)}
                                  title={lt.is_active ? "Deactivate" : "Activate"}
                                >
                                  <Power size={14} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </main>
          </div>

            {/* ── Add / Edit Modal ── */}
            {isOpen && (
              <div className="lt-overlay">
                <div className="lt-modal">

                  {/* Header */}
                  <div className="lt-modal-header">
                    <div>
                      <h3>{editItem ? "Edit Leave Type" : "Create Leave Type"}</h3>
                      <p>{editItem ? "Modify the leave type and its configuration." : "Define a new leave type and its configuration."}</p>
                    </div>
                    <button className="lt-modal-close" onClick={() => setIsOpen(false)}>
                      <X size={18} />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="lt-modal-body">

                    {/* Row 1: Name + Code */}
                    <div className="lt-form-row">
                      <div className="lt-form-group">
                        <label>Name</label>
                        <input
                          className={`lt-input ${errors.name ? "has-error" : ""}`}
                          value={formData.name}
                          onChange={e => set("name", e.target.value)}
                          placeholder="e.g. Casual Leave"
                        />
                        {errors.name && <p className="lt-error">{errors.name}</p>}
                      </div>
                      <div className="lt-form-group">
                        <label>Code</label>
                        <input
                          className={`lt-input ${errors.code ? "has-error" : ""}`}
                          value={formData.code}
                          onChange={e => set("code", e.target.value.toUpperCase())}
                          placeholder="e.g. CL"
                          maxLength={10}
                        />
                        {errors.code && <p className="lt-error">{errors.code}</p>}
                      </div>
                    </div>

                    {/* Row 2: Unit Type + Accrual Type - DYNAMIC */}
                    <div className="lt-form-row">
                      <div className="lt-form-group">
                        <label>Unit Type {dropdownsLoading.UintType && <small>Loading...</small>}</label>
                        <select 
                          className="lt-input lt-select" 
                          value={formData.unit_type} 
                          onChange={e => set("unit_type", e.target.value)}
                          disabled={dropdownsLoading.UintType}
                        >
                          {getDropdownOptions('UintType').map((option) => (
                            <option key={option.dropdown_value_code} value={option.dropdown_value_code}>
                              {option.dropdown_value_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="lt-form-group">
                        <label>Accrual Type {dropdownsLoading.Accrualtype && <small>Loading...</small>}</label>
                        <select 
                          className="lt-input lt-select" 
                          value={formData.accrual_type} 
                          onChange={e => set("accrual_type", e.target.value)}
                          disabled={dropdownsLoading.Accrualtype}
                        >
                          {getDropdownOptions('Accrualtype').map((option) => (
                            <option key={option.dropdown_value_code} value={option.dropdown_value_code}>
                              {option.dropdown_value_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Row 3: Max Consecutive Days */}
                    <div className="lt-form-group">
                      <label>Max Consecutive Days</label>
                      <input
                        type="number"
                        className="lt-input"
                        value={formData.max_consecutive_days}
                        onChange={e => set("max_consecutive_days", e.target.value)}
                        placeholder="e.g. 5"
                        min={0}
                      />
                    </div>

                    {/* Row 4: Gender Restriction + Max Carry Forward - DYNAMIC */}
                    <div className="lt-form-row">
                      <div className="lt-form-group">
                        <label>Gender Restriction {dropdownsLoading.GenderRestriction && <small>Loading...</small>}</label>
                        <select 
                          className="lt-input lt-select" 
                          value={formData.gender_restriction} 
                          onChange={e => set("gender_restriction", e.target.value)}
                          disabled={dropdownsLoading.GenderRestriction}
                        >
                          {getDropdownOptions('GenderRestriction').map((option) => (
                            <option key={option.dropdown_value_code} value={option.dropdown_value_code}>
                              {option.dropdown_value_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="lt-form-group">
                        <label>Max Carry Forward</label>
                        <input
                          type="number"
                          className="lt-input"
                          value={formData.max_carry_forward}
                          onChange={e => set("max_carry_forward", Number(e.target.value))}
                          min={0}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    {/* Toggle rows */}
                    <div className="lt-toggles-box">
                      <div className="lt-toggle-row">
                        <span className="lt-toggle-label">Carry Forward</span>
                        <Toggle value={formData.carry_forward_allowed} onChange={v => set("carry_forward_allowed", v)} />
                      </div>
                      <div className="lt-toggle-row lt-toggle-border">
                        <span className="lt-toggle-label">Requires Document</span>
                        <Toggle value={formData.requires_document} onChange={v => set("requires_document", v)} />
                      </div>
                      <div className="lt-toggle-row lt-toggle-border">
                        <span className="lt-toggle-label">Direct Head Approval</span>
                        <Toggle value={formData.reporting_head_override} onChange={v => set("reporting_head_override", v)} />
                      </div>
                      <div className="lt-toggle-row lt-toggle-border">
                        <span className="lt-toggle-label">Active</span>
                        <Toggle value={formData.is_active} onChange={v => set("is_active", v)} />
                      </div>
                    </div>

                  </div>

                  {/* Footer */}
                  <div className="lt-modal-footer">
                    <button className="btn-cancel" onClick={() => setIsOpen(false)} disabled={submitting}>Cancel</button>
                    <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
                      {submitting ? "Saving…" : editItem ? "Save Changes" : "Create"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Toggle Confirm ── */}
            {toggleId && (
              <div className="lt-overlay">
                <div className="lt-delete-modal">
                  <div className="lt-delete-icon">
                    <Power size={24} />
                  </div>
                  <h3>
                    {leaveTypes.find(lt => lt.id === toggleId)?.is_active
                      ? "Deactivate Leave Type?"
                      : "Activate Leave Type?"
                    }
                  </h3>
                  <p>This will affect all related leave policies.</p>
                  <div className="lt-delete-actions">
                    <button className="btn-cancel" onClick={() => setToggleId(null)}>Cancel</button>
                    <button className="btn-primary" onClick={() => handleToggle(toggleId)}>Confirm</button>
                  </div>
                </div>
              </div>
            )}

            {/* Removed custom toast, react-hot-toast is handled globally */}
    </>
  );

  if (isTab) return pageContent;

  return (
    <>
      <div className="page container">
        {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}
        <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <main className="main">
          <div className="hamburger" onClick={toggleSidebar}>☰</div>
          {pageContent}
          <Footer />
        </main>
      </div>
    </>
  )
}
